/* Minimog variant-picker + FartMart cascading options.
   Each dropdown only lists values that exist for the options chosen before it
   (Fit -> Color -> Size), and changing one option snaps the later ones to a real
   variant before the section re-render, so the page never lands on a combination
   that does not exist. The matrix comes from [data-fm-variant-matrix] in
   snippets/product-variant-picker.liquid. Without a matrix the picker behaves
   exactly like stock Minimog. */
if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll('.m-product-option select, .m-product-option input');
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });
      return result;
    }

    connectedCallback() {
      this.applyCascade();
      this.addEventListener('change', (event) => {
        const target = this.getInputForEventTarget(event.target);
        const resolved = this.resolveSelection(event.target);
        const selectedOptionValues = resolved || this.selectedOptionValues;
        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, { data: { selectedOptionValues, event, target } });
      });
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }

    /* ---------- FartMart cascade ---------- */

    get matrix() {
      if (this._matrix !== undefined) return this._matrix;
      this._matrix = null;
      const el = this.querySelector('[data-fm-variant-matrix]');
      if (!el) return this._matrix;
      try {
        const data = JSON.parse(el.textContent);
        if (data && Array.isArray(data.v) && data.v.length) this._matrix = data;
      } catch (e) {
        console.warn('variant-picker: bad variant matrix', e);
      }
      return this._matrix;
    }

    get fields() {
      return Array.from(this.querySelectorAll('[data-picker-field]')).map((wrap) => ({
        wrap,
        select: wrap.querySelector('select'),
        nodes: Array.from(wrap.querySelectorAll('.m-product-option--node')),
      }));
    }

    valueOf(field) {
      if (field.select) {
        const opt = Array.from(field.select.options).find((o) => o.selected && o.dataset.optionValueId);
        return opt ? opt.value : '';
      }
      const checked = field.wrap.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : (field.wrap.dataset.selectedValue || '');
    }

    allowedValues(k, selected) {
      const m = this.matrix;
      const allowed = new Set();
      m.v.forEach((row) => {
        for (let j = 0; j < k; j++) {
          if (row[j + 1] !== selected[j]) return;
        }
        allowed.add(row[k + 1]);
      });
      return allowed;
    }

    applyCascade() {
      const m = this.matrix;
      if (!m) return;
      const fields = this.fields;
      const selected = fields.map((f) => this.valueOf(f));

      fields.forEach((field, k) => {
        const allowed = this.allowedValues(k, selected);
        if (field.select) {
          const select = field.select;
          if (!select._fmAll) select._fmAll = Array.from(select.options);
          const current = this.valueOf(field);
          const keep = select._fmAll.filter((o) => !o.dataset.optionValueId || allowed.has(o.value));
          select.innerHTML = '';
          keep.forEach((o) => select.appendChild(o));
          const match = keep.find((o) => o.value === current && o.dataset.optionValueId);
          if (match) match.selected = true;
        } else {
          field.nodes.forEach((node) => {
            const value = node.dataset.value;
            node.hidden = value !== undefined && !allowed.has(value);
          });
        }
      });
    }

    resolveSelection(changedEl) {
      const m = this.matrix;
      if (!m) return null;
      const fields = this.fields;
      const k = fields.findIndex((f) => f.wrap.contains(changedEl));
      if (k < 0) return null;
      const selected = fields.map((f) => this.valueOf(f));

      let candidates = m.v;
      for (let j = 0; j <= k; j++) {
        const sub = candidates.filter((row) => row[j + 1] === selected[j]);
        if (!sub.length) break;
        candidates = sub;
      }
      for (let j = k + 1; j < fields.length; j++) {
        const sub = candidates.filter((row) => row[j + 1] === selected[j]);
        if (sub.length) candidates = sub;
      }
      const pick = candidates.find((row) => row[0]) || candidates[0];
      if (!pick) return null;

      const ids = [];
      fields.forEach((field, j) => {
        const name = pick[j + 1];
        const pool = field.select
          ? (field.select._fmAll || Array.from(field.select.options))
          : Array.from(field.wrap.querySelectorAll('input[type="radio"]'));
        const hit = pool.find((o) => o.value === name && o.dataset.optionValueId);
        if (hit) ids.push(hit.dataset.optionValueId);
      });
      return ids.length === fields.length ? ids : null;
    }
  }
  customElements.define("variant-picker", VariantPicker);
}
